import { useIntl } from 'react-intl'
import { useState } from 'react'
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import Card from '@/entities/Card';
import { getPercent } from '@/shared/utils/formatters';
import * as StatsIndicator from '@/shared/ui/StatsIndicator';
import { SharesChart } from '~/accountingAndAnonymous/Shares';
import { useGetMinerSharedQuery } from '@/shared/api/Hashrates';
import {
  selectDateRange,
  selectPollingInterval,
} from "@/processes/Settings";
import { useMinerAccount } from "@/processes/MinerAccounts";

type SharesWidgetProps = {
  className?: string;
}

const SharesWidget = (props: SharesWidgetProps) => {
  const { className } = props;
  const intl = useIntl();
  const dateRange = useSelector(selectDateRange);
  const pollingInterval = useSelector(selectPollingInterval);
  const { miner, account, coin } = useMinerAccount();
  const [visibleChartBars, setVisibleChartBars] = useState([]);

  const accountOrMiner = account || miner;

  const { data = {}, isSuccess } = useGetMinerSharedQuery({
    miner: accountOrMiner,
    coin,
    dateRange,
  }, { pollingInterval, skip: !(accountOrMiner && coin) });

  const validSharesTotal = String(_get(data, 'validCount', 0));
  const staleSharesTotal = String(_get(data, 'staleCount', 0));
  const invalidSharesTotal = String(_get(data, 'invalidCount', 0));
  const validRateTotal = _get(data, 'validRate', 0);
  const staleRateTotal = _get(data, 'staleRate', 0);
  const invalidRateTotal = _get(data, 'invalidRate', 0);

  return (
    <Card className={className} title={intl.formatMessage({ id: 'shares' })}>
      <StatsIndicator.Group
        className="flex -mx-2 my-4"
        selectedItems={visibleChartBars}
        onChange={(items) => setVisibleChartBars(items)}
      >
        <StatsIndicator.Item
          name="validCount"
          size='l'
          className='mx-2'
          status='success'
          isLoading={!isSuccess}
          title={intl.formatMessage({ id: 'shares.valid' })}
          value={validSharesTotal}
          subValue={validRateTotal ? getPercent(validRateTotal) : ''}
        />
        <StatsIndicator.Item
          name="staleCount"
          size='l'
          className='mx-2'
          status='warning'
          isLoading={!isSuccess}
          title={intl.formatMessage({ id: 'shares.stale' })}
          value={staleSharesTotal}
          subValue={staleRateTotal ? getPercent(staleRateTotal) : ''}
        />
        <StatsIndicator.Item
          name="invalidCount"
          size='l'
          className='mx-2'
          status='error'
          isLoading={!isSuccess}
          title={intl.formatMessage({ id: 'shares.invalid' })}
          value={invalidSharesTotal}
          subValue={invalidRateTotal ? getPercent(invalidRateTotal) : ''}
        />
      </StatsIndicator.Group>

      <SharesChart visibleChartBars={visibleChartBars}/>
    </Card>
  );
}

export default SharesWidget;
